import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SearchInput from '../../components/SearchInput/SearchInput';
import Button from '../../components/Button/Button';
import TemplateCard from '../../components/TemplateCard/TemplateCard';
import ConfirmationModal from '../../components/Modals/ConfirmationModal/ConfirmationModal';
import TemplateModal from '../../components/Modals/TemplateModal/TemplateModal';
import DietTypePickerModal from '../../components/Modals/DietTypePickerModal/DietTypePickerModal';
import TourButton from '../../components/TourButton/TourButton';
import { getDietTemplatesPageSteps } from '../../tours/dietTemplatesPage';
import { useNotification } from '../../components/notifications/useNotification';
import { muiMenuItemSx } from '../../utils/functions';
import FormControl from '@mui/material/FormControl';
import MuiSelect from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import InputLabel from '@mui/material/InputLabel';
import { useMediaQuery } from '@mui/material';
import './DietTemplates.css';

const LIMIT_OPTIONS = [6, 12, 24, 48];
const CALORIC_BUCKETS = [
    '<800', '800-1100', '1100-1400', '1400-1700', '1700-2000',
    '2000-2300', '2300-2600', '2600-2900', '2900-3200',
    '3200-3500', '3500-3800', '3800-4100', '>4100',
];
const DIET_TYPE_OPTIONS = [
    'omnivore', 'vegan', 'vegetarian', 'carnivore', 'pescatarian',
    'mediterranean', 'ketogenic', 'paleo', 'gluten_free', 'lactose_free',
];

const buildEmptyTemplateDietPlan = (isAlternatives = false) => isAlternatives
    ? { day0: {}, day1: {}, day2: {}, day3: {} }
    : { day0: {}, day1: {}, day2: {}, day3: {}, day4: {}, day5: {}, day6: {} };

const getAvgKcal = (dietPlan) => {
    let total = 0, days = 0;
    Object.values(dietPlan ?? {}).forEach(day => {
        if (typeof day !== 'object' || !day) return;
        let dayKcal = 0, hasFood = false;
        Object.values(day).forEach(meal => {
            if (!Array.isArray(meal?.foods)) return;
            meal.foods.forEach(f => { dayKcal += parseFloat(f.kcal ?? 0); hasFood = true; });
        });
        if (hasFood) { total += dayKcal; days++; }
    });
    return days > 0 ? Math.round(total / days) : 0;
};

const matchesBucket = (kcal, bucket) => {
    if (!bucket) return true;
    if (bucket === '<800') return kcal < 800;
    if (bucket === '>4100') return kcal > 4100;
    const [lo, hi] = bucket.split('-').map(Number);
    return kcal >= lo && kcal < hi;
};

const muiLimitSx = {
    '&:before': { borderBottomColor: 'var(--tertiary-color)' },
    '&:hover:not(.Mui-disabled):before': { borderBottomColor: 'var(--secondary-color)' },
    '&:after': { borderBottomColor: 'var(--primary-color)' },
    '& .MuiSelect-select': { padding: '2px 24px 2px 4px', fontSize: '0.8rem', fontWeight: 600 },
    '& .MuiSelect-icon': { color: 'var(--secondary-color)', right: 0 },
};

const intoleranceSx = {
    flex: 1,
    width: '100%',
    maxWidth: 360,
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: 'var(--secondary-color)' },
        '&.Mui-focused fieldset': { borderColor: 'var(--primary-color)' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: 'var(--primary-color)' },
    '& .MuiChip-root': {
        height: 20, fontSize: 11,
        backgroundColor: 'rgba(253, 71, 1, 0.1)',
        color: 'var(--primary-color)',
        '& .MuiChip-deleteIcon': { color: 'var(--primary-color)', fontSize: 14 },
    },
};

const illnessSx = {
    flex: 1,
    width: '100%',
    maxWidth: 360,
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: '#93c5fd' },
        '&.Mui-focused fieldset': { borderColor: '#3b82f6' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#3b82f6' },
    '& .MuiChip-root': {
        height: 20, fontSize: 11,
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        color: '#3b82f6',
        '& .MuiChip-deleteIcon': { color: '#3b82f6', fontSize: 14 },
    },
};

const caloricSx = {
    minWidth: 220,
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: '#a3a3a3' },
        '&.Mui-focused fieldset': { borderColor: '#64748b' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#64748b' },
};

const dietTypeSx = {
    minWidth: 180,
    '& .MuiOutlinedInput-root': {
        '&:hover fieldset': { borderColor: '#a3a3a3' },
        '&.Mui-focused fieldset': { borderColor: '#64748b' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#64748b' },
};

const DietTemplates = () => {
    const { t, i18n } = useTranslation();
    const { showError, showSuccess } = useNotification();
    const lang = i18n.language?.startsWith('it') ? 'it' : 'en';
    const isMobile = useMediaQuery('(max-width: 768px)');

    const [templates, setTemplates] = useState([]);
    const [allIntolerances, setAllIntolerances] = useState([]);
    const [allIllnesses, setAllIllnesses] = useState([]);
    const [loading, setLoading] = useState(true);

    const [searchFilter, setSearchFilter] = useState('');
    const [limit, setLimit] = useState(12);
    const [currentPage, setCurrentPage] = useState(1);
    const [intoleranceFilter, setIntoleranceFilter] = useState([]);
    const [illnessFilter, setIllnessFilter] = useState([]);
    const [caloricFilter, setCaloricFilter] = useState('');
    const [dietTypeFilter, setDietTypeFilter] = useState('');

    const [templateModal, setTemplateModal] = useState(null);
    const [showCreateTypePrompt, setShowCreateTypePrompt] = useState(false);
    const [deleteTemplate, setDeleteTemplate] = useState(null);

    const resolveLocalizedName = (item) => {
        if (!item) return '';
        if (typeof item === 'string') return item;
        if (typeof item.name === 'object') return item.name[lang] || item.name.en || item.name.it || '';
        return item.name ?? '';
    };

    const fetchData = async () => {
        setLoading(true);
        try {
            const [templatesRes, intolerancesRes, illnessesRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/diet-templates`, { withCredentials: true }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/food-intolerances`, { withCredentials: true }),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/illnesses`, { withCredentials: true }),
            ]);
            setTemplates(Array.isArray(templatesRes.data) ? templatesRes.data : []);
            setAllIntolerances(Array.isArray(intolerancesRes.data) ? intolerancesRes.data : []);
            setAllIllnesses(Array.isArray(illnessesRes.data) ? illnessesRes.data : []);
        } catch (error) {
            console.error('Failed to fetch templates page data:', error);
            showError(t('unknown-error'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredTemplates = useMemo(() => {
        const search = searchFilter.trim().toLowerCase();
        return templates.filter((template) => {
            const title = (template.title || '').toLowerCase();
            if (search && !title.includes(search)) return false;

            if (intoleranceFilter.length > 0) {
                const ids = (template.intolerances ?? []).map(i => i?._id);
                if (!intoleranceFilter.every(f => ids.includes(f._id))) return false;
            }

            if (illnessFilter.length > 0) {
                const ids = (template.illnesses ?? []).map(i => i?._id);
                if (!illnessFilter.every(f => ids.includes(f._id))) return false;
            }

            if (caloricFilter) {
                const avgKcal = getAvgKcal(template.dietPlan);
                if (avgKcal === 0 || !matchesBucket(avgKcal, caloricFilter)) return false;
            }

            if (dietTypeFilter) {
                if ((template.dietType ?? 'omnivore') !== dietTypeFilter) return false;
            }

            return true;
        });
    }, [templates, searchFilter, intoleranceFilter, illnessFilter, caloricFilter, dietTypeFilter]);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchFilter, limit, intoleranceFilter, illnessFilter, caloricFilter, dietTypeFilter]);

    const totalPages = Math.max(1, Math.ceil(filteredTemplates.length / limit));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * limit;
    const pagedTemplates = filteredTemplates.slice(startIndex, startIndex + limit);

    const showingFrom = filteredTemplates.length === 0 ? 0 : startIndex + 1;
    const showingTo = startIndex + pagedTemplates.length;

    const pageWindow = () => {
        const pages = [];
        for (let i = Math.max(1, safeCurrentPage - 2); i <= Math.min(totalPages, safeCurrentPage + 2); i++) {
            pages.push(i);
        }
        return pages;
    };

    const openCreateEditor = () => {
        setShowCreateTypePrompt(true);
    };

    const openCreateEditorByType = (isAlternatives) => {
        setShowCreateTypePrompt(false);
        setTemplateModal({
            mode: 'create',
            template: {
                title: '',
                language: lang,
                dietPlan: buildEmptyTemplateDietPlan(isAlternatives),
                intolerances: [],
                illnesses: [],
                isAlternatives,
                dietType: 'omnivore',
            },
        });
    };

    const openEditEditor = (template) => {
        setTemplateModal({ mode: 'edit', template });
    };

    const handleDeleteTemplate = async () => {
        if (!deleteTemplate?._id) return;
        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/diet-templates/${deleteTemplate._id}`, {
                withCredentials: true,
            });
            setDeleteTemplate(null);
            showSuccess(t('template-deleted'));
            await fetchData();
        } catch (error) {
            console.error('Failed to delete template:', error);
            showError(t('unknown-error'));
        }
    };

    return (
        <div className="dt-page">
            <div className="dt-header" data-tour="dtp-search">
                <SearchInput
                    name="templates-search-input"
                    value={searchFilter}
                    placeholder={t('search-template-by-name')}
                    onChange={(e) => setSearchFilter(e.target.value)}
                />
                <Button
                    label={t('new-template')}
                    onClick={openCreateEditor}
                    style={{ width: '180px', whiteSpace: 'nowrap' }}
                />
                <TourButton steps={getDietTemplatesPageSteps(t)} iconOnly={isMobile} label={t('tour.tutorial-btn')} />
            </div>

            <div className="dt-filters-row">
                <span className="dt-total-count">
                    {t('table-showing-templates', {
                        from: showingFrom,
                        to: showingTo,
                        total: filteredTemplates.length,
                    })}
                </span>
                <div className="dt-limit-wrap">
                    <FormControl variant="standard" size="small">
                        <MuiSelect
                            variant="standard"
                            value={limit.toString()}
                            onChange={(e) => setLimit(parseInt(e.target.value, 10))}
                            sx={muiLimitSx}
                        >
                            {LIMIT_OPTIONS.map((n) => (
                                <MenuItem key={n} value={n.toString()} sx={muiMenuItemSx}>{n}</MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>
                </div>
            </div>

            <div className="dt-advanced-filters">
                <div className="dt-advanced-filters-row1">
                    <Autocomplete
                        multiple
                        size="small"
                        options={allIntolerances}
                        value={intoleranceFilter}
                        onChange={(_, value) => setIntoleranceFilter(value)}
                        getOptionLabel={(o) => resolveLocalizedName(o)}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        renderInput={(params) => (
                            <TextField {...params} label={t('intolerances')} />
                        )}
                        sx={intoleranceSx}
                        data-tour="dtp-intolerances"
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        options={allIllnesses}
                        value={illnessFilter}
                        onChange={(_, value) => setIllnessFilter(value)}
                        getOptionLabel={(o) => resolveLocalizedName(o)}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        renderInput={(params) => (
                            <TextField {...params} label={t('illnesses')} />
                        )}
                        sx={illnessSx}
                        data-tour="dtp-illnesses"
                    />
                    <FormControl size="small" sx={caloricSx} data-tour="dtp-caloric">
                        <InputLabel>{t('caloric-range')} (kcal)</InputLabel>
                        <MuiSelect
                            value={caloricFilter}
                            label={`${t('caloric-range')} (kcal)`}
                            onChange={(e) => setCaloricFilter(e.target.value)}
                        >
                            <MenuItem value=""><em>{t('all')}</em></MenuItem>
                            {CALORIC_BUCKETS.map((bucket) => (
                                <MenuItem key={bucket} value={bucket}>{bucket} kcal</MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>
                    <FormControl size="small" sx={dietTypeSx} data-tour="dtp-diettype">
                        <InputLabel>{t('diet-type')}</InputLabel>
                        <MuiSelect
                            value={dietTypeFilter}
                            label={t('diet-type')}
                            onChange={(e) => setDietTypeFilter(e.target.value)}
                        >
                            <MenuItem value=""><em>{t('all')}</em></MenuItem>
                            {DIET_TYPE_OPTIONS.map((type) => (
                                <MenuItem key={type} value={type}>{t(type)}</MenuItem>
                            ))}
                        </MuiSelect>
                    </FormControl>
                </div>
            </div>

            <div className="dt-cards-grid" data-tour="dtp-grid">
                {loading ? (
                    <p className="dt-empty">{t('loading')}...</p>
                ) : pagedTemplates.length === 0 ? (
                    <p className="dt-empty">{t('no-templates-found')}</p>
                ) : (
                    pagedTemplates.map((template) => (
                        <TemplateCard
                            key={template._id}
                            template={template}
                            onClick={() => openEditEditor(template)}
                            showActionRow={true}
                            onEdit={() => openEditEditor(template)}
                            onDelete={() => setDeleteTemplate(template)}
                        />
                    ))
                )}
            </div>

            <div className="dt-footer" data-tour="dtp-pagination">
                <div className="dt-pagination">
                    <button
                        className="dt-pag-btn"
                        disabled={safeCurrentPage === 1}
                        onClick={() => setCurrentPage(1)}
                        aria-label="first page"
                    >«</button>
                    <button
                        className="dt-pag-btn"
                        disabled={safeCurrentPage === 1}
                        onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                        aria-label="previous page"
                    >‹</button>
                    {pageWindow().map((p) => (
                        <button
                            key={p}
                            className={`dt-pag-btn${p === safeCurrentPage ? ' dt-pag-btn--active' : ''}`}
                            onClick={() => setCurrentPage(p)}
                        >
                            {p}
                        </button>
                    ))}
                    <button
                        className="dt-pag-btn"
                        disabled={safeCurrentPage === totalPages}
                        onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                        aria-label="next page"
                    >›</button>
                    <button
                        className="dt-pag-btn"
                        disabled={safeCurrentPage === totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        aria-label="last page"
                    >»</button>
                </div>
            </div>

            {showCreateTypePrompt && (
                <DietTypePickerModal
                    title={t('new-template')}
                    onWeekly={() => openCreateEditorByType(false)}
                    onAlternatives={() => openCreateEditorByType(true)}
                    onClose={() => setShowCreateTypePrompt(false)}
                />
            )}

            {templateModal && (
                <TemplateModal
                    mode={templateModal.mode}
                    template={templateModal.template}
                    allIntolerances={allIntolerances}
                    allIllnesses={allIllnesses}
                    setShowModal={setTemplateModal}
                    onSave={fetchData}
                />
            )}

            {deleteTemplate && (
                <ConfirmationModal
                    titleText={`${t('delete-template')}: ${deleteTemplate.title}`}
                    bodyText={t('confirm-delete-template')}
                    handleConfirm={handleDeleteTemplate}
                    handleClose={() => setDeleteTemplate(null)}
                />
            )}
        </div>
    );
};

export default DietTemplates;
